import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataFilterTable } from "Components/Form/DataFilterTable";
import { WithModal, withModal } from "Components/General/Modal/withModal";
import { I18N_NS } from "Config/i18n";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { Alert, Button, Checkbox } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalApplicationsForm } from "./ExternalApplicationsForm";

export const ExternalApplications = withModal(({ showModal }: WithModal) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<API_ClientApp[]>([]);
  const [hideDcr, setHideDcr] = useState(true);
  const { t } = useTranslation(I18N_NS.ADMIN);

  const fetch = async () => {
    try {
      setLoading(true);
      const response = await API.getData<API_ClientApp[]>(API_URL.THIRD_PARTY_ADMIN.getAllExternalApps());
      setData(response);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getClientIdAndSecret = async () => {
    try {
      const response = await API.getData<API_ClientInformation>(API_URL.THIRD_PARTY_ADMIN.generateClientInformation());
      return response;
    } catch (error) {
      throw error;
    }
  };

  const columns: ColumnsType<API_ClientApp> = [
    {
      dataIndex: "id",
      title: "ID",
      width: 100,
    },
    {
      dataIndex: "clientId",
      title: "Client ID",
      width: 400,
    },
    {
      dataIndex: "name",
      title: "Name",
    },
    {
      dataIndex: "businessUserId",
      title: "User ID",
      width: 200,
    },
    {
      dataIndex: "dcr",
      title: t("DCR"),
      width: 90,
      render: (dcr: boolean | undefined) => (dcr ? t("Yes") : t("No")),
    },
    {
      dataIndex: "id",
      title: t("Actions"),
      width: 100,
      render: (_v, record) => (
        <Button
          size="small"
          type="link"
          aria-label={t("Edit External Application")}
          onClick={() => showDetails(record)}
        >
          <FontAwesomeIcon icon={["far", "edit"]} fixedWidth={true} />
        </Button>
      ),
    },
  ];

  const handleCreate = async (values) => {
    try {
      await API.newData(API_URL.THIRD_PARTY_ADMIN.addExternalApp(), values);
      fetch();
    } catch (error) {
      throw error;
    }
  };

  const handleEdit = async (values) => {
    try {
      await API.editData(API_URL.THIRD_PARTY_ADMIN.updateExternalApp(values.id), values);
      fetch();
    } catch (error) {
      throw error;
    }
  };

  const showDetails = (initialData?: API_ClientApp) => {
    showModal(ExternalApplicationsForm, {
      modalProps: {
        footer: null,
        width: 800,
        title: initialData ? t("Edit External Application") : t("Create External Application"),
      },

      componentProps: {
        onSubmit: initialData ? handleEdit : handleCreate,
        initialData,
        isNew: !initialData,
        getClientIdAndSecret: getClientIdAndSecret,
      },
    });
  };

  const createNewApp = () => {
    showDetails();
  };

  const filterPredicate = (query: string) => (record: API_ClientApp) => {
    return Object.values(record).some((value) => value && value.toString?.().indexOf(query) !== -1);
  };

  useEffect(() => {
    fetch();
  }, []);

  return (
    <div className="g-card">
      <Alert
        className="mb-2"
        type="info"
        showIcon
        message={t(
          "Dynamically registered OAuth clients (for example Cursor) appear here too. " +
            "They are not the MCP resource-server application. Hidden by default."
        )}
      />
      <Checkbox className="mb-2" checked={hideDcr} onChange={(event) => setHideDcr(event.target.checked)}>
        {t("Hide dynamically registered clients (DCR)")}
      </Checkbox>
      <DataFilterTable
        name="ExternalApplications"
        searchOnChange={true}
        filterPredicate={filterPredicate}
        rowKey="id"
        dataSource={hideDcr ? data.filter((app) => !app.dcr) : data}
        loading={loading}
        pagination={false}
        actionButtons={[
          {
            type: "default",
            iconName: "sync-alt",
            onClick: fetch,
            label: t("Refresh"),
          },
          {
            type: "primary",
            iconName: "plus",
            onClick: createNewApp,
            label: t("New Application"),
          },
        ]}
        rowClassName={(record) => (record.active ? "" : "disabledCell")}
        placeholder={t("Search")}
        columns={columns}
      />
    </div>
  );
});
